import { createReducer } from '@reduxjs/toolkit'
import { createSelector } from 'reselect'

import {
  getTutorStats,
  getStudentStats,
  getMap,
  getStudentCalendar,
  getStudentPastClasses,
  getTutorPastClasses,
  getStudentUpcomingClasses,
  getTutorUpcomingClasses,
} from './dashboard.action'
import { logout } from '../user/user.action'

const initialState = {
  topTutors: [],
  topCategories: [],
  tutorStats: [],
  studentStats: [],
  studentQuizzesStats: null,
  tutorProfileStats: null,
  mapData: null,
  studentCalendarData: [],
  tutorNextClass: null,

  tutorPastClasses: {
    tutorPastClasses: [],
    tutorPastClassesPage: 0,
    tutorPastClassesPageSize: 0,
    tutorPastClassesTotal: 0,
  },

  tutorUpcomingClasses: {
    tutorUpcomingClasses: [],
    tutorUpcomingClassesPage: 0,
    tutorUpcomingClassesPageSize: 0,
    tutorUpcomingClassesTotal: 0,
  },

  studentUpcomingClasses: {
    studentUpcomingClasses: [],
    studentUpcomingClassesPage: 0,
    studentUpcomingClassesPageSize: 0,
    studentUpcomingClassesTotal: 0,
  },

  studentPastClasses: {
    studentPastClasses: [],
    studentPastClassesPage: 0,
    studentPastClassesPageSize: 0,
    studentPastClassesTotal: 0,
  },

  loading: {
    getTutorStats: false,
    getStudentStats: false,
    getMap: false,
    getStudentPastClasses: false,
    getTutorPastClasses: false,
    getStudentUpcomingClasses: false,
    getTutorUpcomingClasses: false,
  },
  error: {
    getTutorStats: false,
    getStudentStats: false,
    getMap: false,
    getStudentPastClasses: false,
    getTutorPastClasses: false,
    getStudentUpcomingClasses: false,
    getTutorUpcomingClasses: false,
  },
}

const dashboardReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(getTutorStats.fulfilled, (state, action) => {
      state.topTutors = action.payload?.top_5_tutors_on_platform
      state.topCategories = action.payload?.top_category_stats
      state.tutorProfileStats = action.payload?.tutor_profile
      state.tutorStats = action.payload?.tutor_statistics_by_period_and_category
      state.tutorNextClass = action.payload?.next_class

      state.loading.getTutorStats = false
      state.error.getTutorStats = false
    })
    .addCase(getTutorStats.pending, (state) => {
      state.loading.getTutorStats = true
      state.error.getTutorStats = false
    })
    .addCase(getTutorStats.rejected, (state) => {
      state.loading.getTutorStats = false
      state.error.getTutorStats = true
    })

    .addCase(getStudentStats.fulfilled, (state, action) => {
      state.topTutors = action.payload?.top_5_tutors_on_platform
      state.topCategories = action.payload?.top_category_stats
      state.studentStats = action.payload?.classes_graphics_by_period_and_category
      state.studentQuizzesStats = action.payload?.stduent_quiz_stats

      state.loading.getStudentStats = false
      state.error.getStudentStats = false
    })
    .addCase(getStudentStats.pending, (state) => {
      state.loading.getStudentStats = true
      state.error.getStudentStats = false
    })
    .addCase(getStudentStats.rejected, (state) => {
      state.loading.getStudentStats = false
      state.error.getStudentStats = true
    })

    .addCase(getMap.fulfilled, (state, action) => {
      state.mapData = action.payload

      state.loading.getMap = false
      state.error.getMap = false
    })
    .addCase(getMap.pending, (state) => {
      state.loading.getMap = true
      state.error.getMap = false
    })
    .addCase(getMap.rejected, (state) => {
      state.loading.getMap = false
      state.error.getMap = true
    })

    .addCase(getStudentCalendar.fulfilled, (state, action) => {
      state.studentCalendarData = action.payload
      state.loading.getStudentStats = false
      state.error.getStudentStats = false
    })
    .addCase(getStudentCalendar.pending, (state) => {
      state.loading.getStudentStats = true
      state.error.getStudentStats = false
    })
    .addCase(getStudentCalendar.rejected, (state) => {
      state.loading.getStudentStats = false
      state.error.getStudentStats = true
    })

    .addCase(getTutorPastClasses.fulfilled, (state, action) => {
      state.tutorPastClasses.tutorPastClasses = action.payload?.results || []
      state.tutorPastClasses.tutorPastClassesPage = action.payload?.page
      state.tutorPastClasses.tutorPastClassesTotal = action.payload?.total
      state.tutorPastClasses.tutorPastClassesPageSize = action.payload?.page_size

      state.loading.getTutorPastClasses = false
      state.error.getTutorPastClasses = false
    })
    .addCase(getTutorPastClasses.pending, (state) => {
      state.loading.getTutorPastClasses = true
      state.error.getTutorPastClasses = false
    })
    .addCase(getTutorPastClasses.rejected, (state) => {
      state.loading.getTutorPastClasses = false
      state.error.getTutorPastClasses = true
    })

    .addCase(getStudentPastClasses.fulfilled, (state, action) => {
      state.studentPastClasses.studentPastClasses = action.payload?.results || []
      state.studentPastClasses.studentPastClassesPage = action.payload?.page
      state.studentPastClasses.studentPastClassesTotal = action.payload?.total
      state.studentPastClasses.studentPastClassesPageSize = action.payload?.page_size

      state.loading.getStudentPastClasses = false
      state.error.getStudentPastClasses = false
    })
    .addCase(getStudentPastClasses.pending, (state) => {
      state.loading.getStudentPastClasses = true
      state.error.getStudentPastClasses = false
    })
    .addCase(getStudentPastClasses.rejected, (state) => {
      state.loading.getStudentPastClasses = false
      state.error.getStudentPastClasses = true
    })

    .addCase(getStudentUpcomingClasses.fulfilled, (state, action) => {
      state.studentUpcomingClasses.studentUpcomingClasses = action.payload?.results || []
      state.studentUpcomingClasses.studentUpcomingClassesPage = action.payload?.page
      state.studentUpcomingClasses.studentUpcomingClassesTotal = action.payload?.total
      state.studentUpcomingClasses.studentUpcomingClassesPageSize = action.payload?.page_size

      state.loading.getStudentUpcomingClasses = false
      state.error.getStudentUpcomingClasses = false
    })
    .addCase(getStudentUpcomingClasses.pending, (state) => {
      state.loading.getStudentUpcomingClasses = true
      state.error.getStudentUpcomingClasses = false
    })
    .addCase(getStudentUpcomingClasses.rejected, (state) => {
      state.loading.getStudentUpcomingClasses = false
      state.error.getStudentUpcomingClasses = true
    })

    .addCase(getTutorUpcomingClasses.fulfilled, (state, action) => {
      state.tutorUpcomingClasses.tutorUpcomingClasses = action.payload?.results || []
      state.tutorUpcomingClasses.tutorUpcomingClassesPage = action.payload?.page
      state.tutorUpcomingClasses.tutorUpcomingClassesTotal = action.payload?.total
      state.tutorUpcomingClasses.tutorUpcomingClassesPageSize = action.payload?.page_size

      state.loading.getTutorUpcomingClasses = false
      state.error.getTutorUpcomingClasses = false
    })
    .addCase(getTutorUpcomingClasses.pending, (state) => {
      state.loading.getTutorUpcomingClasses = true
      state.error.getTutorUpcomingClasses = false
    })
    .addCase(getTutorUpcomingClasses.rejected, (state) => {
      state.loading.getTutorUpcomingClasses = false
      state.error.getTutorUpcomingClasses = true
    })

    .addCase(logout.fulfilled, () => initialState)

    .addDefaultCase((state) => state)
})

export default dashboardReducer

const dashboardState = (state) => state.dashboard

export const selectTopTutors = createSelector(dashboardState, ({ topTutors }) => topTutors)

export const selectTopCategories = createSelector(
  dashboardState,
  ({ topCategories }) => topCategories
)

export const selectTutorStats = createSelector(dashboardState, ({ tutorStats }) => tutorStats)

export const selectStudentStats = createSelector(dashboardState, ({ studentStats }) => studentStats)

export const selectStudentQuizzesStats = createSelector(
  dashboardState,
  ({ studentQuizzesStats }) => studentQuizzesStats
)

export const selectTutorProfileStats = createSelector(
  dashboardState,
  ({ tutorProfileStats }) => tutorProfileStats
)

export const selectLoadingGetTutorStats = createSelector(
  dashboardState,
  ({ loading: { getTutorStats } }) => getTutorStats
)

export const selectErrorGetTutorStats = createSelector(
  dashboardState,
  ({ error: { getTutorStats } }) => getTutorStats
)

export const selectMapData = createSelector(dashboardState, ({ mapData }) => mapData)

export const selectStudentCalendarData = createSelector(dashboardState, ({ studentCalendarData }) =>
  studentCalendarData.reduce((acc, item) => {
    acc[`${item.availability_calendar_detail.day}:${item.availability_calendar_detail.slot}`] = item
    return acc
  }, {})
)

export const selectLoadingStudentPastClasses = createSelector(
  dashboardState,
  ({ loading: { getStudentPastClasses } }) => getStudentPastClasses
)

export const selectStudentPastClassesPagination = createSelector(
  dashboardState,
  ({
    studentPastClasses: {
      studentPastClasses,
      studentPastClassesPage,
      studentPastClassesPageSize,
      studentPastClassesTotal,
    },
  }) => ({
    studentPastClasses,
    studentPastClassesPage,
    studentPastClassesPageSize,
    studentPastClassesTotal,
  })
)

export const selectLoadingTutorPastClasses = createSelector(
  dashboardState,
  ({ loading: { getTutorPastClasses } }) => getTutorPastClasses
)

export const selectTutorPastClassesPagination = createSelector(
  dashboardState,
  ({
    tutorPastClasses: {
      tutorPastClasses,
      tutorPastClassesPage,
      tutorPastClassesPageSize,
      tutorPastClassesTotal,
    },
  }) => ({
    tutorPastClasses,
    tutorPastClassesPage,
    tutorPastClassesPageSize,
    tutorPastClassesTotal,
  })
)

export const selectLoadingStudentUpcomingClasses = createSelector(
  dashboardState,
  ({ loading: { getStudentUpcomingClasses } }) => getStudentUpcomingClasses
)

export const selectStudentUpcomingClassesPagination = createSelector(
  dashboardState,
  ({
    studentUpcomingClasses: {
      studentUpcomingClasses,
      studentUpcomingClassesPage,
      studentUpcomingClassesPageSize,
      studentUpcomingClassesTotal,
    },
  }) => ({
    studentUpcomingClasses,
    studentUpcomingClassesPage,
    studentUpcomingClassesPageSize,
    studentUpcomingClassesTotal,
  })
)

export const selectLoadingTutorUpcomingClasses = createSelector(
  dashboardState,
  ({ loading: { getTutorUpcomingClasses } }) => getTutorUpcomingClasses
)

export const selectTutorUpcomingClassesPagination = createSelector(
  dashboardState,
  ({
    tutorUpcomingClasses: {
      tutorUpcomingClasses,
      tutorUpcomingClassesPage,
      tutorUpcomingClassesPageSize,
      tutorUpcomingClassesTotal,
    },
  }) => ({
    tutorUpcomingClasses,
    tutorUpcomingClassesPage,
    tutorUpcomingClassesPageSize,
    tutorUpcomingClassesTotal,
  })
)

export const selectTutorNextClass = createSelector(
  dashboardState,
  ({ tutorNextClass }) => tutorNextClass
)
